import i18next, {t} from 'i18next';
import {days, months} from '../utils/constants';

export function formatWeddingOccasionTime(dateString: string, weddingOfficiantName: string): string {
  return t('booking.occasionInfo', {
    dateTime: formatDateToReadableString(new Date(dateString)),
    weddingOfficiantName,
  });
}

/**
 * Converts a local date to a readable string.
 * The format of the string is e.g "Lördag 12 november kl. 13:00".
 * If the date is not valid, an empty string is returned.
 * @param {Date} date - The local date.
 * @returns {string} The formatted date string.
 */
export function formatDateToReadableString(date: Date) {
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return '';
  }

  const time = `${date.getHours() < 10 ? '0' : ''}${date.getHours()}:${
    date.getMinutes() < 10 ? '0' : ''
  }${date.getMinutes()}`;
  const dayOfWeek = date.getDay();
  const dayOfMonth = date.getDate();
  const month = date.getMonth();
  const year = date.getUTCFullYear();
  const formattedDate = `${days[dayOfWeek]} ${dayOfMonth} ${months[month]} ${year} kl. ${time}`;
  return formattedDate;
}

/**
 * Given an array of date strings and a reference date, returns the nearest date in the array
 * for the same day as the reference date. Returns null if no dates are found for that day.
 *
 * @param dates - The array of date strings to search through
 * @param date - The reference date
 * @returns The nearest date string for the same day as the reference date, or null if no dates are found
 */
export function getNearestDate(dates: string[], date: Date): string | null {
  // Filter the dates array to include only the dates that have the same day
  const sameDayDates = dates.filter((dateString) => {
    const parsedDate = new Date(dateString);
    return parsedDate.getDate() === date.getDate();
  });

  if (sameDayDates.length === 0) {
    return null;
  }

  // Convert each date string to a Unix timestamp in milliseconds
  const dateTimes = sameDayDates.map((dateString) => {
    const parsedDate = new Date(dateString);
    parsedDate.setUTCHours(parsedDate.getUTCHours() - parsedDate.getTimezoneOffset() / 60);
    return parsedDate.getTime();
  });

  // Find the date in the array with the smallest difference from the reference date
  const nearestDate = dateTimes.reduce((prev, curr) => {
    return Math.abs(curr - date.getTime()) < Math.abs(prev - date.getTime()) ? curr : prev;
  });

  return new Date(nearestDate).toISOString();
}

/**
 * Given an array of date strings and a reference date string, returns an array of
 * the nearest date for each unique day in the month of the reference date.
 *
 * @param dates - The array of date strings to search through
 * @param date - The reference date string in ISO format (e.g. "2022-04-15T00:00:00.000Z")
 * @returns An array of date strings representing the nearest date for each unique day in the month
 */
export function getNearestDatesForEachDayInMonth(dates: string[], date: string): string[] {
  const nearestDates: string[] = [];

  // Parse the year, month, and number of days in the month from the reference date
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Loop through each day in the month
  for (let i = 1; i <= daysInMonth; i++) {
    const day = new Date(year, month, i);
    const nearestDate = getNearestDate(dates, day);

    if (nearestDate && !nearestDates.includes(nearestDate)) {
      nearestDates.push(nearestDate);
    }
  }

  return nearestDates;
}
/**
 * Given a date string in ISO format (e.g. "2022-04-15T00:00:00.000Z"),
 * in the format "Month Year" (e.g. "April 2022").
 * @param date as string
 * @returns string, e.g "April 2022"
 */
export function getMonthYearFormatFromDate(date: string): string {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  const monthName = months[month][0].toUpperCase() + months[month].slice(1);
  return `${monthName} ${year}`;
}

/**
 * Given an array of date strings, returns a sorted list of the unique months and years
 * represented in the array. For each unique month and year, only the most recent date
 * is included in the output.
 *
 * @param dates - The array of date strings to search through
 * @returns A sorted list of the unique month and year strings in the array, with only the most recent date for each month and year
 */
export function getUniqueListOfMonthsFromDates(dates: string[]): string[] {
  // Create a Set to store the unique date strings and a Set to store the unique month/year strings
  const uniqueDates = new Set<string>();
  const uniqueMonthYears = new Set<string>();

  // Loop through each date string in the array
  for (const date of dates) {
    // Get the month and year string for the current date string
    const monthYear = getMonthYearFormatFromDate(date);

    // If the month/year string is not already in the uniqueMonthYears Set, add the date string to the uniqueDates Set
    // and add the month/year string to the uniqueMonthYears Set
    if (!uniqueMonthYears.has(monthYear)) {
      uniqueDates.add(date);
      uniqueMonthYears.add(monthYear);
    }
  }

  // Convert the uniqueDates Set to an array, sort it in descending order by date, convert each date back to an ISO string,
  // and reverse the order to get the most recent date for each month/year string first
  const sortedDates = Array.from(uniqueDates)
    .map((dateString) => new Date(dateString))
    .sort((dateA, dateB) => dateB.getTime() - dateA.getTime())
    .map((date) => date.toISOString())
    .reverse();

  return sortedDates;
}

/**
 * Converts a local Date object to a string in the specified format.
 * @param {Date} date - The date to be converted.
 * @param {string} format - The format for the date string. Can be 'YYYY-MM-DD', 'HH:mm', or 'YYYY-MM-DD HH:mm'.
 * @returns {string} The formatted date string.
 */
export function convertDateToString(date: Date, format?: 'YYYY-MM-DD' | 'HH:mm' | 'YYYY-MM-DD HH:mm'): string {
  // Define the default format for the date string
  let formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };

  // Modify the format options based on the `format` parameter
  switch (format) {
    case 'YYYY-MM-DD':
      formatOptions = {year: 'numeric', month: '2-digit', day: '2-digit'};
      break;
    case 'HH:mm':
      formatOptions = {hour: '2-digit', minute: '2-digit'};
      break;
    case 'YYYY-MM-DD HH:mm':
    default:
      break;
  }

  // Convert the date to a string using the defined format
  const formattedDate = date.toLocaleString(undefined, formatOptions);

  return formattedDate;
}

export function getTranslatedErrorMessage(message?: string): string {
  return i18next.exists('apiResponse.' + message) ? t('apiResponse.' + message) : t('apiResponse.INTERNAL_ERROR');
}
